import React, { useEffect, useState, useMemo } from 'react';
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  Chip,
  Typography,
  Alert,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
  TableSortLabel,
  SelectChangeEvent
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Star as StarIcon, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { 
  getAccounts, 
  createAccount, 
  updateAccount, 
  deleteAccount, 
  getAccountById 
} from '../../services/homeExpensesApi';
import { AccountDto, ExpenseType } from '../../types/homeExpenses';
import AccountForm from './AccountForm';

interface AccountListProps {
  expenseType?: ExpenseType;
}

const AccountList: React.FC<AccountListProps> = ({ expenseType }) => {
  const [accounts, setAccounts] = useState<AccountDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<AccountDto | null>(null);
  
  // Filtering and sorting state
  const [searchTerm, setSearchTerm] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');
  const [sortBy, setSortBy] = useState<keyof AccountDto>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0); // Reset to first page when searching
  };

  const handleAccountTypeChange = (event: SelectChangeEvent<string>) => {
    setAccountTypeFilter(event.target.value);
    setPage(0); // Reset to first page when filtering
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchAccounts = async () => {
    setLoading(true);
    setError(null);
    try {
      setAccounts(await getAccounts(expenseType));
    } catch (e: any) {
      setError(e.message || 'Falha ao carregar as contas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchAccounts(); 
  }, [expenseType]);

  // Filtered and sorted data
  const filteredAndSortedAccounts = useMemo(() => {
    let filtered = [...accounts];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(account => 
        account.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (account.description && account.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply account type filter
    if (accountTypeFilter === 'primary') {
      filtered = filtered.filter(account => account.isPrimary);
    } else if (accountTypeFilter === 'secondary') {
      filtered = filtered.filter(account => !account.isPrimary);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortBy as keyof AccountDto] || '';
      const bValue = b[sortBy as keyof AccountDto] || '';
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      if (typeof aValue === 'boolean' && typeof bValue === 'boolean') {
        const comparison = Number(bValue) - Number(aValue); // Primary accounts first
        return sortOrder === 'asc' ? comparison : -comparison;
      }
      
      return 0;
    });

    return filtered;
  }, [accounts, searchTerm, accountTypeFilter, sortBy, sortOrder]);

  // Paginated data
  const paginatedAccounts = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredAndSortedAccounts.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedAccounts, page, rowsPerPage]);

  // Total count for pagination
  const totalAccounts = filteredAndSortedAccounts.length;

  const handleSort = (property: keyof AccountDto) => {
    const isAsc = sortBy === property && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setAccountTypeFilter('');
    setPage(0);
  };

  const handleAdd = () => {
    setEditing(null);
    setShowForm(true);
  };

  const handleEdit = async (publicId: string) => {
    setLoading(true);
    try {
      const account = await getAccountById(publicId);
      setEditing(account);
      setShowForm(true);
    } catch (e: any) {
      setError(e.message || 'Falha ao carregar os detalhes da conta');
    } finally { 
      setLoading(false); 
    }
  };

  const handleDelete = async (publicId: string) => {
    if (!window.confirm('Tem a certeza de que pretende eliminar esta conta?')) return;
    setLoading(true);
    try {
      await deleteAccount(publicId);
      fetchAccounts();
    } catch (e: any) {
      setError(e.message || 'Falha ao eliminar a conta');
    } finally { 
      setLoading(false); 
    }
  };

  const handleFormSubmit = async (data: {
    name: string;
    description?: string;
    color?: string;
    isPrimary: boolean;
  }) => {
    setLoading(true);
    try {
      if (editing && editing.publicId) {
        await updateAccount(
          editing.publicId,
          data.name,
          data.description,
          data.color,
          data.isPrimary
        );
      } else {
        await createAccount(data.name, data.description, data.color, data.isPrimary, expenseType);
      }
      setShowForm(false);
      setEditing(null);
      fetchAccounts();
    } catch (e: any) {
      setError(e.message || 'Falha ao guardar a conta');
    } finally { 
      setLoading(false); 
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditing(null);
  };

  if (loading && accounts.length === 0) {
    return <Typography>A carregar contas...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Contas</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={loading}
        >
          Adicionar conta
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtering Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <Box sx={{ flex: '1 1 300px', minWidth: '200px' }}>
            <TextField
              fullWidth
              size="small"
              label="Pesquisar contas..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />
              }}
            />
          </Box>
          <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo de conta</InputLabel>
              <Select
                value={accountTypeFilter}
                onChange={handleAccountTypeChange}
                label="Tipo de conta"
              >
                <MenuItem value="">Todas as contas</MenuItem>
                <MenuItem value="primary">Só principais</MenuItem>
                <MenuItem value="secondary">Só secundárias</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ClearIcon />}
            onClick={clearFilters}
            disabled={!searchTerm && !accountTypeFilter}
          >
            Limpar
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            A mostrar {paginatedAccounts.length} de {filteredAndSortedAccounts.length} contas
          </Typography>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'name'}
                  direction={sortBy === 'name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('name')}
                >
                  Nome
                </TableSortLabel>
              </TableCell>
              <TableCell>Descrição</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'isPrimary'}
                  direction={sortBy === 'isPrimary' ? sortOrder : 'asc'}
                  onClick={() => handleSort('isPrimary')}
                >
                  Tipo
                </TableSortLabel>
              </TableCell>
              <TableCell>Cor</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedAccounts.map((account) => (
              <TableRow key={account.publicId}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {account.name}
                    {account.isPrimary && (
                      <StarIcon color="primary" fontSize="small" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>{account.description || '-'}</TableCell>
                <TableCell>
                  <Chip 
                    label={account.isPrimary ? 'Principal' : 'Secundária'}
                    color={account.isPrimary ? 'primary' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {account.color && (
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: account.color,
                        border: '1px solid #ccc'
                      }}
                    />
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => handleEdit(account.publicId)}
                    disabled={loading}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete(account.publicId)}
                    disabled={loading}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {accounts.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    Nenhuma conta encontrada. Adicione a sua primeira conta para começar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <TablePagination
          component="div"
          count={totalAccounts}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`}
        />
      </Box>

      <Dialog 
        open={showForm} 
        onClose={handleFormCancel}
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>
          {editing ? 'Editar conta' : 'Adicionar nova conta'}
        </DialogTitle>
        <DialogContent>
          <AccountForm 
            initial={editing || undefined}
            onSubmit={handleFormSubmit}
            onCancel={handleFormCancel}
            loading={loading}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AccountList;
